import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import { worldKeys } from './world';

// Server: quests/QuestStepDto.java. A locked step (no quest_progress row yet for this account)
// carries name = "???" and narrative = null; progress/target are null for a locked step. Never
// "available" at step level — that state only applies to the whole chain, before step 1 exists.
export type QuestStepDto = {
  stepNo: number;
  name: string;
  narrative: string | null;
  state: 'locked' | 'active' | 'complete';
  progress: number | null;
  target: number | null;
};

// Server: quests/QuestChainDto.java. landCode/title/npcCode/npcName are chain-level content
// metadata; state is "available" (never accepted), "active" (accepted, incomplete), or "complete".
export type QuestChainDto = {
  chainCode: string;
  landCode: string;
  title: string;
  npcCode: string;
  npcName: string;
  state: 'available' | 'active' | 'complete';
  steps: QuestStepDto[];
};

export type QuestsDto = {
  chains: QuestChainDto[];
};

export const questsKeys = {
  all: ['quests'] as const,
};

export const getQuests = () => apiFetch<QuestsDto>('/quests');

export function useQuests() {
  return useQuery({ queryKey: questsKeys.all, queryFn: getQuests });
}

export const acceptChain = (chainCode: string) =>
  apiFetch<QuestChainDto>(`/quests/${chainCode}/accept`, { method: 'POST', body: JSON.stringify({}) });

// Not optimistic — the server owns whether accepting is even valid (409 already_accepted is a
// real, expected rejection). Invalidates the whole chains list on success rather than hand-writing
// this one chain into the cache, mirroring useForage's "simpler and just as correct" reasoning.
//
// Final review I2: also invalidates the world snapshot. `GET /world` now carries a per-viewer
// `quest_new`/`quest_step` status per place (task-7), but `QuestGiverSheet` is a sheet OVER
// `LandScreen` — nothing remounts on accept, so without this the land keeps showing "New" plus its
// pin dot for a chain the player just accepted, until they leave and come back.
export function useAcceptChain() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (chainCode: string) => acceptChain(chainCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: questsKeys.all });
      queryClient.invalidateQueries({ queryKey: worldKeys.all });
    },
  });
}
